// =============================================================================
// Jedno źródło mapowań statusów organu druku na język panelu (Badge/etykieta/
// symbol) oraz progów czasowych. Każda zakładka czyta STĄD, bo dwa miejsca
// z własnym progiem „offline" to dwa różne zdania o tym samym serwerze —
// dokładnie klasa rozjazdów, którą organ druku ma eliminować.
//
// Zasada naczelna (plan §7): UI nigdy nie twierdzi więcej, niż system wie.
// Stąd stany „nieraportowany" i „nieznany" — brak wiersza statusu to brak
// wiedzy, nie „gotowa".
// =============================================================================

import type { PrintJobStatus, PrintPurpose } from '@insolitum/types';

// ── Progi czasowe ────────────────────────────────────────────────────────────

/**
 * Agent jest offline po 3 zgubionych heartbeatach (heartbeat co 60 s w pętli
 * agenta — `packages/print-agent`). 180 s, nie mniej: jeden zgubiony pakiet
 * na hal-Wi-Fi nie może malować floty na czerwono.
 */
export const PROG_OFFLINE_MS = 3 * 60_000;

/** Fallback poll Kolejki, gdy Realtime nie działa (D10: poll 15 s). */
export const POLL_FALLBACK_MS = 15_000;

/**
 * Ile czekamy na `SUBSCRIBED` kanału Realtime, zanim uznamy go za martwy
 * i włączymy poll. Tabele `print_*` mogą jeszcze nie być w publikacji
 * `supabase_realtime` (migracja M2/M4) — wtedy kanał nigdy nie dostarczy NIC
 * i bez tego progu Kolejka byłaby zamrożona na zawsze.
 */
export const LIMIT_SUBSKRYPCJI_MS = 3_000;

/** Komunikat sweepera print-core: porzucone zadanie po wyczerpaniu prób. */
export const BLAD_DEAD_LETTER = 'stale_claim_dead_letter';

export type WariantBadge = 'default' | 'success' | 'warning' | 'error' | 'info';

// ── Statusy zadań (`print_job_status`, migracja 186) ─────────────────────────

export interface OpisStatusuZadania {
  etykieta: string;
  wariant: WariantBadge;
  symbol: string;
}

export const STATUS_ZADANIA: Record<PrintJobStatus, OpisStatusuZadania> = {
  queued: { etykieta: 'w kolejce', wariant: 'default', symbol: '◔' },
  picked: { etykieta: 'przejęte', wariant: 'info', symbol: '●' },
  printing: { etykieta: 'drukuje', wariant: 'info', symbol: '●' },
  done: { etykieta: 'gotowe', wariant: 'success', symbol: '✓' },
  error: { etykieta: 'błąd', wariant: 'error', symbol: '✗' },
  cancelled: { etykieta: 'anulowane', wariant: 'default', symbol: '⊘' },
};

/**
 * Pełny opis zadania do wiersza tabeli. Dead-letter dostaje osobną etykietę
 * „porzucone" — to sygnał „agent umarł w trakcie druku", inny niż zwykły błąd
 * drukarki i wymagający innej reakcji operatora.
 */
export function opiszZadanie(zadanie: { status: PrintJobStatus; error?: string | null }): {
  etykieta: string;
  wariant: WariantBadge;
  symbol: string;
  porzucone: boolean;
} {
  const opis = STATUS_ZADANIA[zadanie.status] ?? STATUS_ZADANIA.queued;
  const porzucone = zadanie.status === 'error' && zadanie.error === BLAD_DEAD_LETTER;
  return {
    ...opis,
    etykieta: porzucone ? 'porzucone' : opis.etykieta,
    porzucone,
  };
}

// ── Stany agentów (serwerów wydruku) ─────────────────────────────────────────

export type StanAgenta = 'online' | 'offline' | 'pending' | 'revoked';

export interface OpisAgenta {
  symbol: string;
  etykieta: string;
  wariant: WariantBadge;
}

export const OPIS_AGENTA: Record<StanAgenta, OpisAgenta> = {
  online: { symbol: '●', etykieta: 'aktywny', wariant: 'success' },
  pending: { symbol: '◐', etykieta: 'oczekuje na rejestrację', wariant: 'warning' },
  offline: { symbol: '○', etykieta: 'offline', wariant: 'error' },
  revoked: { symbol: '⊘', etykieta: 'odwołany', wariant: 'default' },
};

/**
 * Stan pochodny agenta: kolumna `status` mówi o cyklu życia (pending/active/
 * revoked), a żywość wynika WYŁĄCZNIE z `last_seen_at` — wiersz „active"
 * z heartbeatem sprzed godziny to serwer offline, nie aktywny.
 */
export function stanAgenta(
  agent: { status: string; last_seen_at: string | null },
  teraz: number = Date.now()
): StanAgenta {
  if (agent.status === 'revoked') return 'revoked';
  if (agent.status === 'pending') return 'pending';
  if (!agent.last_seen_at) return 'offline';
  return teraz - new Date(agent.last_seen_at).getTime() <= PROG_OFFLINE_MS ? 'online' : 'offline';
}

/**
 * Banner zdrowia strony: „serwer druku offline" ma sens tylko wtedy, gdy
 * organizacja W OGÓLE ma zarejestrowany serwer (agent `active`), a żaden
 * z nich nie daje znaku życia. Organizacja bez agentów nie jest „offline" —
 * jest przed wdrożeniem i banner byłby fałszywym alarmem.
 */
export function czySerwerDrukuOffline(
  agenci: Array<{ status: string; last_seen_at: string | null }>,
  teraz: number = Date.now()
): boolean {
  const aktywni = agenci.filter((a) => a.status === 'active');
  if (aktywni.length === 0) return false;
  return aktywni.every((a) => stanAgenta(a, teraz) === 'offline');
}

// ── Stany drukarek (pochodne: kartoteka × żywość agenta × print_printer_status) ──

export type StanDrukarki =
  | 'gotowa'
  | 'blad'
  | 'offline'
  | 'agent_offline'
  | 'nieaktywna'
  | 'nieraportowany';

export interface OpisDrukarki {
  etykieta: string;
  wariant: WariantBadge;
  symbol: string;
  szczegol?: string;
}

/**
 * `print_printers` nie ma kolumny statusu — status jest POCHODNY i uczciwy:
 * martwy agent przykrywa wszystko (drukarka jest tak żywa jak jej agent),
 * raport z `print_printer_status` (migracja M4) mówi resztę, a brak raportu
 * to „nieraportowany", nigdy udawane „gotowa".
 */
export function stanDrukarki(wejscie: {
  drukarka: { is_active: boolean; agent_id: string | null };
  agent?: { status: string; last_seen_at: string | null } | null;
  raport?: { status: string; detail: string | null } | null;
  teraz?: number;
}): OpisDrukarki {
  const { drukarka, agent, raport } = wejscie;
  const teraz = wejscie.teraz ?? Date.now();

  if (!drukarka.is_active) {
    return { etykieta: 'nieaktywna', wariant: 'default', symbol: '⊘' };
  }
  if (drukarka.agent_id && agent && stanAgenta(agent, teraz) !== 'online') {
    return {
      etykieta: 'offline',
      wariant: 'error',
      symbol: '○',
      szczegol: 'agent niedostępny',
    };
  }
  if (raport) {
    if (raport.status === 'ready') return { etykieta: 'gotowa', wariant: 'success', symbol: '●' };
    if (raport.status === 'error') {
      return {
        etykieta: 'błąd',
        wariant: 'error',
        symbol: '✗',
        szczegol: raport.detail ?? undefined,
      };
    }
    return {
      etykieta: 'offline',
      wariant: 'error',
      symbol: '○',
      szczegol: raport.detail ?? undefined,
    };
  }
  // Agent żyje (albo drukarka nie ma agenta), ale nikt statusu nie raportuje —
  // mówimy dokładnie tyle, ile wiemy.
  return { etykieta: 'nieraportowany', wariant: 'default', symbol: '◌' };
}

// ── Protokoły i zgodność format ↔ przeznaczenie ──────────────────────────────

/**
 * Protokoły z CHECK-a `print_printers.protocol` (186 + rozszerzenie 623
 * o ESCPOS). Pomoc kontekstowa trafia do formularza dodawania drukarki —
 * lekcja PackFlow: surowy PDF wysłany na drukarkę EPL drukuje śmieci, więc
 * wybór protokołu musi być decyzją świadomą, nie zgadywanką.
 */
export const PROTOKOLY: Array<{ wartosc: string; etykieta: string; pomoc: string }> = [
  {
    wartosc: 'RAW',
    etykieta: 'RAW',
    pomoc: 'Surowe bajty na TCP:9100 — gdy sterownik/protokół nieznany.',
  },
  { wartosc: 'ZPL', etykieta: 'ZPL', pomoc: 'Etykiety termiczne Zebra i zgodne.' },
  { wartosc: 'EPL', etykieta: 'EPL', pomoc: 'Etykiety termiczne (starsze Zebra/XPrinter).' },
  { wartosc: 'TSPL', etykieta: 'TSPL', pomoc: 'Etykiety termiczne TSC/XPrinter.' },
  { wartosc: 'ESCPOS', etykieta: 'ESC/POS', pomoc: 'Drukarki paragonowe (kasowe).' },
  { wartosc: 'PDF', etykieta: 'PDF', pomoc: 'Wyłącznie drukarki z natywnym interpreterem PDF.' },
];

/** Protokoły drukarek termicznych — PDF wysłany na nie drukuje śmieci. */
export const PROTOKOLY_TERMICZNE = ['ZPL', 'EPL', 'TSPL', 'ESCPOS'] as const;

/**
 * Heurystyka nazwy drukarki termicznej — TYLKO do ostrzeżenia w formularzu
 * (PDF + nazwa wyglądająca na termiczną), nigdy do blokowania: nazwa to nie
 * dowód, a admin może wiedzieć lepiej.
 */
export function wygladaNaTermiczna(nazwa: string): boolean {
  return /zebra|xprinter|x-?printer|godex|tsc\b|citizen|argox|pos-?\d|thermal|termi/i.test(nazwa);
}

/**
 * Zgodność przeznaczenie → protokoły drukarki. Blokada niezgodności następuje
 * W MIEJSCU KONFIGURACJI (zakładka Przypisania) — nie dopiero w agencie z
 * papierem w ręku. Słownik przeznaczeń: `PRINT_PURPOSES` w `@insolitum/types`.
 */
export const PROTOKOLY_PRZEZNACZEN: Record<PrintPurpose, readonly string[]> = {
  // Etykiety — świat termiczny (RAW zostaje: gotowe bajty pod dowolny język).
  product_label: ['ZPL', 'EPL', 'TSPL', 'RAW'],
  shipping_label: ['ZPL', 'EPL', 'TSPL', 'RAW'],
  package_label: ['ZPL', 'EPL', 'TSPL', 'RAW'],
  // Dokumenty — wyłącznie PDF (sterowniki / drukarki z interpreterem PDF).
  packing_list: ['PDF'],
  invoice: ['PDF'],
  wz: ['PDF'],
  // Strona testowa istnieje per format — pasuje wszędzie.
  test_page: ['ZPL', 'EPL', 'TSPL', 'ESCPOS', 'RAW', 'PDF'],
};

/**
 * Filtruje kartotekę do drukarek zgodnych z przeznaczeniem. Drukarka `system`
 * (sterownik) bez protokołu przyjmuje PDF — to jest jej kontrakt (plan §6.1:
 * druk na sterowniku = `format='PDF'` + drukarka `system`).
 */
export function drukarkiZgodne<
  T extends { protocol: string | null; type: string; is_active: boolean },
>(przeznaczenie: PrintPurpose, drukarki: T[]): T[] {
  const dozwolone = PROTOKOLY_PRZEZNACZEN[przeznaczenie] ?? [];
  return drukarki.filter((d) => {
    if (!d.is_active) return false;
    const protokol = d.protocol ?? (d.type === 'system' ? 'PDF' : null);
    return protokol !== null && dozwolone.includes(protokol);
  });
}

// ── Formatowanie czasu (konwencja pl-PL jak DevicesPage) ─────────────────────

export function formatujDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** „42 s temu" / „3 min temu" / „2 godz. temu" — do linii heartbeatu. */
export function formatujCzasWzgledny(iso: string | null, teraz: number = Date.now()): string {
  if (!iso) return 'nigdy';
  const delta = Math.max(0, teraz - new Date(iso).getTime());
  const s = Math.floor(delta / 1000);
  if (s < 60) return `${s} s temu`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min temu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} godz. temu`;
  return `${Math.floor(h / 24)} dni temu`;
}

/** Odliczanie w dół („17:42") — do wygaśnięcia kodu parowania. Wygasły → null. */
export function odliczanieDo(isoWygasa: string, teraz: number = Date.now()): string | null {
  const zostalo = new Date(isoWygasa).getTime() - teraz;
  if (zostalo <= 0) return null;
  const s = Math.floor(zostalo / 1000);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}
